import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, SectionTitle, EmptyState, PageLoader, StatusBadge } from "@/lib/ui";
import {
  ShieldCheck, ListTodo, Bell, FileClock, Bot, Cpu, Activity,
  FileText, MessageSquareQuote, Images as ImagesIcon, IndianRupee, PieChart as PieIcon,
  BarChart3, Sparkles, ArrowUpRight, LineChart,
} from "lucide-react";
import { cn } from "@/lib/utils";

const hsl = (v) => (v ? `hsl(${v})` : "hsl(46 68% 52%)");
const fmtINR = (n) => {
  const x = Number(n || 0);
  if (x >= 1e7) return `₹${(x / 1e7).toFixed(2)}Cr`;
  if (x >= 1e5) return `₹${(x / 1e5).toFixed(2)}L`;
  if (x >= 1e3) return `₹${(x / 1e3).toFixed(1)}K`;
  return `₹${x.toLocaleString("en-IN")}`;
};

const KPI_DEFS = [
  { key: "pending_approval", label: "Needs Approval", icon: ShieldCheck, route: "/approvals" },
  { key: "tasks_due", label: "Tasks Due", icon: ListTodo, route: "/tasks" },
  { key: "content_awaiting_review", label: "Content Review", icon: FileClock, route: "/approvals" },
  { key: "unread_notifications", label: "Notifications", icon: Bell, route: "/notifications" },
  { key: "active_agents", label: "Active AI Agents", icon: Bot, route: "/agents" },
  { key: "ai_tokens_used", label: "AI Tokens", icon: Cpu, route: "/agents" },
];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="neu-card rounded-lg px-3 py-2 text-xs">
      <div className="font-medium text-foreground mb-0.5">{label || payload[0]?.name}</div>
      <div className="font-mono text-primary">{fmtINR(payload[0]?.value)}</div>
    </div>
  );
}

function FinancialOverview({ fin, navigate }) {
  const portals = fin?.per_portal || [];
  const data = portals.map((p) => ({ name: p.name.replace("Gopi Gadhvi Group", "GGG"), code: p.code, revenue: p.revenue_inr, accent: p.accent }));
  const nonZero = data.filter((d) => d.revenue > 0);
  const total = fin?.total_inr || 0;

  return (
    <section className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="exec-financials">
      <div className="lg:col-span-8 neu-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /><SectionTitle>Revenue by Portal</SectionTitle></div>
          <StatusBadge tone="gold">Total {fmtINR(total)}</StatusBadge>
        </div>
        <div className="h-[280px] w-full">
          {total === 0 ? (
            <div className="h-full grid place-items-center">
              <EmptyState icon={IndianRupee} title="Awaiting revenue" hint="Live Razorpay payments and per-brand finance metrics populate this chart as they arrive." />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: "hsl(0 0% 63%)", fontSize: 11 }} axisLine={{ stroke: "hsl(0 0% 13%)" }} tickLine={false} interval={0} />
                <YAxis tickFormatter={fmtINR} tick={{ fill: "hsl(0 0% 63%)", fontSize: 11 }} axisLine={false} tickLine={false} width={64} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(0 0% 100% / 0.03)" }} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]} maxBarSize={64}>
                  {data.map((d) => <Cell key={d.code} fill={hsl(d.accent)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        {total === 0 && (
          <p className="text-xs text-muted-foreground mt-2">No paid revenue yet across the group.</p>
        )}
      </div>

      <div className="lg:col-span-4 neu-card rounded-2xl p-5 flex flex-col">
        <div className="flex items-center gap-2 mb-4"><PieIcon className="h-4 w-4 text-primary" /><SectionTitle>Revenue Split</SectionTitle></div>
        {nonZero.length === 0 ? (
          <div className="flex-1 grid place-items-center">
            <EmptyState icon={IndianRupee} title="Awaiting revenue" hint="Once portals collect payments, the split shows here." />
          </div>
        ) : (
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={nonZero} dataKey="revenue" nameKey="name" cx="50%" cy="50%" innerRadius={58} outerRadius={92} paddingAngle={3} stroke="none">
                  {nonZero.map((d) => <Cell key={d.code} fill={hsl(d.accent)} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, color: "hsl(0 0% 63%)" }} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
}

function PortalAnalytics({ analytics, navigate }) {
  return (
    <section data-testid="exec-analytics">
      <div className="flex items-center gap-2 mb-3"><LineChart className="h-4 w-4 text-primary" /><SectionTitle>Portal Analytics Overview</SectionTitle></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {(analytics || []).map((p) => {
          const primary = (p.items && p.items[0]) || null;
          return (
            <button key={p.code} onClick={() => navigate(`/growth/${p.code}`)} data-testid={`portal-card-${p.code}`}
              className="neu-card neu-hover rounded-2xl p-4 text-left" style={{ borderTop: `2px solid ${hsl(p.accent)}` }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: hsl(p.accent) }} />
                <span className="text-xs font-medium truncate">{p.name}</span>
              </div>
              {primary ? (
                <>
                  <div className="font-head text-2xl font-bold" style={{ color: hsl(p.accent) }}>{primary.value}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-0.5 truncate">{primary.label}</div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className={cn("text-[10px] font-mono px-1.5 py-0.5 rounded", p.ga4_connected ? "bg-ok/15 text-ok" : "bg-white/5 text-muted-foreground/60")}>GA4</span>
                    <span className={cn("text-[10px] font-mono px-1.5 py-0.5 rounded", p.pixel_connected ? "bg-ok/15 text-ok" : "bg-white/5 text-muted-foreground/60")}>Pixel</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70 mt-2">
                    {p.ga4_connected || p.pixel_connected ? "Connected · awaiting data" : "Not connected"}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ApprovalsAndTasks({ approvals, tasks, navigate }) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="exec-approvals-tasks">
      <div className="neu-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /><SectionTitle>Pending Approvals</SectionTitle></div>
          <button onClick={() => navigate("/approvals")} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="see-approvals">View all <ArrowUpRight className="h-3 w-3" /></button>
        </div>
        {(approvals?.recent || []).length === 0 ? (
          <EmptyState icon={ShieldCheck} title="Nothing awaiting approval" hint="Items needing sign-off across all portals surface here." />
        ) : (
          <div className="space-y-2">
            {approvals.recent.map((a) => (
              <button key={a.id} onClick={() => navigate("/approvals")} data-testid={`approval-${a.id}`} className="w-full neu-inset rounded-lg px-3 py-2.5 text-left flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="text-sm truncate">{a.title}</div><div className="text-[11px] text-muted-foreground font-mono">{a.brand_id} · {a.category || "general"}</div></div>
                <StatusBadge tone="warn" dot>Review</StatusBadge>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="neu-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><ListTodo className="h-4 w-4 text-primary" /><SectionTitle>Open Tasks</SectionTitle></div>
          <button onClick={() => navigate("/tasks")} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="see-tasks">View all <ArrowUpRight className="h-3 w-3" /></button>
        </div>
        {(tasks?.recent || []).length === 0 ? (
          <EmptyState icon={ListTodo} title="No open tasks" hint="Tasks in progress across the group appear here." />
        ) : (
          <div className="space-y-2">
            {tasks.recent.map((t) => (
              <button key={t.id} onClick={() => navigate("/tasks")} data-testid={`task-${t.id}`} className="w-full neu-inset rounded-lg px-3 py-2.5 text-left flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="text-sm truncate">{t.title}</div><div className="text-[11px] text-muted-foreground font-mono">{t.brand_id} · {t.status}</div></div>
                <StatusBadge tone={t.status === "IN PROGRESS" ? "info" : "muted"} dot>{t.status === "IN PROGRESS" ? "Active" : "To do"}</StatusBadge>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function AIRecommendations({ recs, navigate }) {
  return (
    <section data-testid="exec-ai-recs">
      <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-primary" /><SectionTitle>AI Analysis & Recommendations</SectionTitle></div>
      {(recs || []).length === 0 ? (
        <div className="neu-card rounded-2xl p-1">
          <EmptyState icon={Sparkles} title="No AI recommendations yet"
            hint="Run the Chief of Staff or an Executive Meeting and the reasoned recommendations from each portal's AI head appear here." />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {recs.map((r) => (
            <div key={r.id} className="neu-inset rounded-xl p-4 fade-up" data-testid={`ai-rec-${r.id}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono text-[11px] text-primary">{r.agent_code}</span>
                <span className="text-xs text-foreground/80 font-medium truncate">{r.agent_name || r.kind}</span>
                {r.brand_id && <StatusBadge tone="muted" className="ml-auto">{r.brand_id}</StatusBadge>}
              </div>
              <p className="text-sm text-foreground/75 leading-relaxed">{r.reason || "Completed an analysis run."}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function WebsiteContentCard({ data, navigate }) {
  const items = [
    { key: "blog_drafts", label: "Blog Drafts", hint: "awaiting review", icon: FileText, route: "/blog" },
    { key: "testimonials_pending", label: "Testimonials", hint: "pending approval", icon: MessageSquareQuote, route: "/testimonials" },
    { key: "gallery_unpublished", label: "Gallery Items", hint: "unpublished", icon: ImagesIcon, route: "/gallery" },
  ];
  const total = data?.total ?? 0;
  return (
    <section className="neu-card rounded-2xl p-5" data-testid="website-content-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><FileClock className="h-4 w-4 text-primary" /><SectionTitle>Website Content · Review Queue</SectionTitle></div>
        <StatusBadge tone={total > 0 ? "warn" : "ok"} dot={total > 0 ? "pulse" : false}>{total > 0 ? `${total} awaiting` : "All clear"}</StatusBadge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {items.map((it) => (
          <button key={it.key} data-testid={`review-${it.key}`} onClick={() => navigate(it.route)} className="neu-inset neu-hover rounded-xl p-4 text-left group">
            <div className="flex items-center justify-between">
              <it.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.75} />
              <span className="font-head text-2xl font-bold">{data?.[it.key] ?? 0}</span>
            </div>
            <div className="text-sm font-medium mt-2">{it.label}</div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mt-0.5">{it.hint}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const api = useApi();
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard", brand], queryFn: () => api.get(`/dashboard?brand_id=${brand}`) });
  const { data: exec, isLoading: execLoading } = useQuery({ queryKey: ["exec-dashboard"], queryFn: () => api.get("/dashboard/executive") });
  const { data: reviewQueue } = useQuery({ queryKey: ["review-queue"], queryFn: () => api.get("/cms/review-queue") });

  if (isLoading || execLoading) return <PageLoader />;
  const c = data?.cards || {};

  return (
    <div className="space-y-8 pb-6">
      <PageHeader kicker={data?.date} title="Executive Command Centre">
        <img src="/ggg-logo.webp" alt="Gopi Gadhvi Group" className="h-9 w-auto hidden sm:block mr-1 opacity-90" />
        <StatusBadge tone="ok" dot="pulse">Systems Online</StatusBadge>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-strip">
        {KPI_DEFS.map((d) => (
          <button key={d.key} data-testid={`dash-card-${d.key}`} onClick={() => navigate(d.route)} className="neu-card neu-hover rounded-xl p-4 text-left group">
            <d.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.75} />
            <div className="font-head text-2xl font-bold mt-3">{c[d.key] ?? 0}</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-1">{d.label}</div>
          </button>
        ))}
      </div>

      <FinancialOverview fin={exec?.financials} navigate={navigate} />
      <PortalAnalytics analytics={exec?.analytics} navigate={navigate} />
      <ApprovalsAndTasks approvals={exec?.approvals} tasks={exec?.tasks} navigate={navigate} />
      <AIRecommendations recs={exec?.ai_recommendations} navigate={navigate} />
      <WebsiteContentCard data={reviewQueue} navigate={navigate} />
    </div>
  );
}
